import React, { ReactElement, useContext } from 'react'

import Fieldset from '../../Common/Components/Fieldset'
import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import TextInput from '../../Common/Components/TextInput'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import Preview from './Preview'

import { LanguageContext } from '../../Common/Contexts'

import { allCurrencyDefs } from '../../Common/Utils/Currency'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { CurrencyDisplayFormat as CurrencyDisplayFormatLanguage } from '../../../../i18n'
import {
    FINANCIAL_DECIMAL_SEPARATOR_OPTION_COLON,
    FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA,
    FINANCIAL_DECIMAL_SEPARATOR_OPTION_FULL_STOP,
    FINANCIAL_GROUPINGSEPARATOR_OPTION_COMMA,
    FINANCIAL_GROUPINGSEPARATOR_OPTION_FULL_STOP,
    FINANCIAL_GROUPINGSEPARATOR_OPTION_SPACE
} from './Constants'
import { CurrencyDisplayFormatSettings } from '../Types'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

/**
 * Component properties type.
 */
type Props = {
    currencyDisplayFormatSettings: CurrencyDisplayFormatSettings
    handleCurrencyDisplayFormatSettingsChange: (newCurrencyDisplayFormatSettings: CurrencyDisplayFormatSettings) => void
    previewValue: string | undefined
}

/**
 * Currency display format section of Financial tab.
 * @param currencyDisplayFormatSettings Object containing all currency formatting settings.
 * @param handleCurrencyDisplayFormatSettingsChange Handler method for changes to currencyDisplayFormatSettings.
 * @param previewValue The value displayed in preview component.
 */
const CurrencyDisplayFormat: React.FC<Props> = ({
    currencyDisplayFormatSettings,
    handleCurrencyDisplayFormatSettingsChange,
    previewValue
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('CurrencyDisplayFormat')
    language.loadBundle('Currency')

    const getCurrenciesAsSelectItems = (): SelectOptions<number> =>
        allCurrencyDefs.map((currencyDef) => ({
            label: language.get(currencyDef.descLangID),
            value: currencyDef.currencyID
        }))

    const groupingSeparatorItems: SelectOptions<number> = [
        {
            label: `, ${language?.get(CurrencyDisplayFormatLanguage.GroupingseparatorOptionCommaDesc)}`,
            value: FINANCIAL_GROUPINGSEPARATOR_OPTION_COMMA
        },
        {
            label: `\u00a0 ${language?.get(CurrencyDisplayFormatLanguage.GroupingseparatorOptionSpaceDesc)}`,
            value: FINANCIAL_GROUPINGSEPARATOR_OPTION_SPACE
        },
        {
            label: `. ${language?.get(CurrencyDisplayFormatLanguage.GroupingseparatorOptionFullstopDesc)}`,
            value: FINANCIAL_GROUPINGSEPARATOR_OPTION_FULL_STOP
        }
    ]

    const decimalSeparatorItems: SelectOptions<number> = [
        {
            label: `. ${language?.get(CurrencyDisplayFormatLanguage.DecimalseparatorOptionFullstopDesc)}`,
            value: FINANCIAL_DECIMAL_SEPARATOR_OPTION_FULL_STOP
        },
        {
            label: `: ${language?.get(CurrencyDisplayFormatLanguage.DecimalseparatorOptionColonDesc)}`,
            value: FINANCIAL_DECIMAL_SEPARATOR_OPTION_COLON
        },
        {
            label: `, ${language?.get(CurrencyDisplayFormatLanguage.DecimalseparatorOptionCommaDesc)}`,
            value: FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA
        }
    ]

    /**
     * Handler method for selecting new currency - it updates all related props (pattern, separators...) when currency is changed.
     * @param newCurrencyId ID of the newly selected currency.
     */
    const handleCurrencyChange = (newCurrencyId: number): void => {
        const newCurrency = allCurrencyDefs.find((currency) => currency.currencyID === newCurrencyId)
        if (newCurrency) {
            handleCurrencyDisplayFormatSettingsChange({
                currencyId: newCurrencyId,
                decimalSeparator: newCurrency.decimalSeparatorDefault.charCodeAt(0),
                groupingSeparator: newCurrency.groupingSeparatorDefault.charCodeAt(0),
                pattern: newCurrency.patternDefault
            })
        }
    }

    const handleGroupingSeparatorChange = (newValue: number): void => {
        let newGroupingSeparator = newValue
        // If the same character is set in both fields, then the grouping separator is automatically changed - a default
        // value of either the ',' (comma) character is automatically applied to the grouping separator setting, or the
        // space character is used as a fallback when the decimal separator is set as a comma already.
        if (currencyDisplayFormatSettings.decimalSeparator === newValue) {
            if (currencyDisplayFormatSettings.decimalSeparator === FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA) {
                // Comma is already used by decimal separator, the grouping separator has to be set to comma.
                newGroupingSeparator = FINANCIAL_GROUPINGSEPARATOR_OPTION_SPACE
            } else {
                newGroupingSeparator = FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA
            }
        }

        handleCurrencyDisplayFormatSettingsChange({
            ...currencyDisplayFormatSettings,
            groupingSeparator: newGroupingSeparator
        })
    }

    const handleDecimalSeparatorChange = (newValue: number): void => {
        const newDecimalSeparator = newValue
        let newGroupingSeparator = currencyDisplayFormatSettings.groupingSeparator

        // Decimal separator has priority - if attempting to change to same character, change groupingSeparator to
        // suitable value - see Help page for Financial tab for more details.
        if (newDecimalSeparator === newGroupingSeparator) {
            if (newDecimalSeparator !== FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA) {
                newGroupingSeparator = FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA
            } else {
                newGroupingSeparator = FINANCIAL_GROUPINGSEPARATOR_OPTION_SPACE
            }
        }

        handleCurrencyDisplayFormatSettingsChange({
            ...currencyDisplayFormatSettings,
            decimalSeparator: newDecimalSeparator,
            groupingSeparator: newGroupingSeparator
        })
    }

    const handlePatternChange = (newValue: string): void => {
        handleCurrencyDisplayFormatSettingsChange({
            ...currencyDisplayFormatSettings,
            pattern: newValue
        })
    }

    return (
        <Fieldset className='currency-display'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(CurrencyDisplayFormatLanguage.CurrencyDisplayFormat_SectionHeader)}
            </Heading>

            <FormRow isInline className={'currency-symbol'}>
                {/* The user is selecting a symbol, but we need to track it as currencyId, since there is no proper way to map between symbol and currency. */}
                <Select
                    label={language?.get(CurrencyDisplayFormatLanguage.SymbolLabel)}
                    options={getCurrenciesAsSelectItems()}
                    value={currencyDisplayFormatSettings.currencyId}
                    handleChange={handleCurrencyChange}
                />
                <TextInput
                    value={currencyDisplayFormatSettings.pattern}
                    onChange={handlePatternChange}
                    labelLeft={language.get(CurrencyDisplayFormatLanguage.PatternLabel)}
                />
            </FormRow>
            <FormRow isInline className={'grouping-separator'}>
                <Select
                    label={language?.get(CurrencyDisplayFormatLanguage.GroupingseparatorLabel)}
                    options={groupingSeparatorItems}
                    value={currencyDisplayFormatSettings.groupingSeparator}
                    handleChange={handleGroupingSeparatorChange}
                />
                <Select
                    label={language?.get(CurrencyDisplayFormatLanguage.DecimalseparatorLabel)}
                    options={decimalSeparatorItems}
                    value={currencyDisplayFormatSettings.decimalSeparator}
                    handleChange={handleDecimalSeparatorChange}
                />
            </FormRow>

            <Preview previewValue={previewValue} />
        </Fieldset>
    )
}

export default React.memo(CurrencyDisplayFormat)
